import React, { useEffect, useState } from "react";
import CryptoJS from "crypto-js";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Divider,
  Grid2,
  Switch,
  IconButton,
  Stack,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DescriptionIcon from "@mui/icons-material/Description";
import HomeIcon from "@mui/icons-material/Home";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ContactsIcon from "@mui/icons-material/Contacts";
import WarningIcon from "@mui/icons-material/Warning";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PaymentIcon from "@mui/icons-material/Payment";
import ReceiptIcon from "@mui/icons-material/Receipt";
import GavelIcon from "@mui/icons-material/Gavel";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { useNavigate, useParams } from "react-router-dom";
import form_datatype from "../../constants/formDataType";
import { trackPromise } from "react-promise-tracker";
import apiClient from "../../lib/api";
import dataConstant from "../../constants/dataConstants";
import urlConstant from "../../constants/urlConstants";
import moment from "moment-timezone";
import { IconChevronLeft, IconPrinter } from "@tabler/icons-react";
import { lightPalette } from "../../theme-layout/palette";
import ClientInfoDetail from "../../components/patientDetail/clientInfoDetail";
import ServiceScheduleDetail from "../../components/patientDetail/serviceScheduleDetail";
import ServiceDetail from "../../components/patientDetail/serviceDetail";
import ServiceAddressDetail from "../../components/patientDetail/serviceAddressDetail";
import CareStatusDetail from "../../components/patientDetail/careStatusDetail";
import AuthorizedContactDetail from "../../components/patientDetail/authorizedContactDetail";
import EmergencyPlanningDetail from "../../components/patientDetail/emergencyPlanningDetail";
import PaymentDetail from "../../components/patientDetail/paymentDetail";
import BillingInformation from "../../components/patientDetail/billingInformation";
import LegalTermsDetail from "../../components/patientDetail/legalTermsDetail";
import ReviewDetail from "../../components/patientDetail/reviewDetails";
import GeneratePdf from "../../components/patientDetail/generatePdf";
import DocumentationDetail from "../../components/patientDetail/documentationDetail";
import { useSelector } from "react-redux";
import { selectUserDetail } from "../../redux/slice/userSlice";

const menuItems = [
  { key: "clientInfo", label: "Client Info", icon: <InfoIcon /> },
  {
    key: "serviceSchedule",
    label: "Service Schedule",
    icon: <CalendarMonthIcon />,
  },
  {
    key: "serviceDetails",
    label: "Service Details",
    icon: <DescriptionIcon />,
  },
  { key: "serviceAddress", label: "Service Address", icon: <HomeIcon /> },
  { key: "careStatus", label: "Care Status", icon: <FavoriteIcon /> },
  {
    key: "authorizedContacts",
    label: "Authorized Contacts",
    icon: <ContactsIcon />,
  },
  {
    key: "emergencyPlanning",
    label: "Emergency Planning",
    icon: <WarningIcon />,
  },
  {
    key: "documentation",
    label: "Documentation",
    icon: <InsertDriveFileIcon />,
  },
  { key: "paymentDetails", label: "Payment Details", icon: <PaymentIcon /> },
  {
    key: "billingInformation",
    label: "Billing Information",
    icon: <ReceiptIcon />,
  },
  { key: "legalTerms", label: "Legal Terms & Agreements", icon: <GavelIcon /> },
  { key: "reviewSubmit", label: "Review", icon: <DoneAllIcon /> },
];

function decrypt(encryptedValue, key) {
  try {
    const type = form_datatype[key];
    const decrypted = CryptoJS.AES.decrypt(
      encryptedValue,
      process.env.REACT_APP_ENCRYPTION_KEY,
    );
    let plaintext = decrypted.toString(CryptoJS.enc.Utf8);

    if (!plaintext) return encryptedValue;

    switch (type) {
      case "boolean":
        return plaintext === "true";
      case "number":
        const num = Number(plaintext);
        return isNaN(num) ? encryptedValue : num;
      case "string":
      default:
        return plaintext;
    }
  } catch {
    return encryptedValue;
  }
}

function decryptObject(obj, parentKey = "") {
  if (Array.isArray(obj)) {
    return obj.map((item) => decryptObject(item, parentKey)); // pass down parentKey
  } else if (typeof obj === "object" && obj !== null) {
    const result = {};
    for (const key in obj) {
      const fullKey = key; // or `${parentKey}.${key}` if you want full path
      result[key] = decryptObject(obj[key], fullKey);
    }
    return result;
  } else if (typeof obj === "string") {
    return decrypt(obj, parentKey);
  } else {
    return obj;
  }
}

const PatientDetail = () => {
  const [activeKey, setActiveKey] = useState("clientInfo");
  const { id } = useParams();
  const navigate = useNavigate();
  const userDetails = useSelector(selectUserDetail);
  const [formData, setFormData] = useState();
  const [isApiCalled, setIsApiCalled] = useState(false);
  const [approveUserDetail, setApproveUserDetail] = useState({
    approvedBy: "",
    approvedDate: "",
  });

  useEffect(() => {
    try {
      trackPromise(
        apiClient
          .get(
            `${urlConstant.Submission.GetSubmissionById}?submissionUnqGuid=${id}`,
            {
              authRequired: true,
            },
          )
          .then(async (response) => {
            if (response.status === dataConstant.APIStatus.Failure) {
            } else {
              const result = await decryptObject(
                response.data.formData.form_data,
              );

              result.insurance = result.insurance || {};
              result.insurance.insuranceTerm =
                result.insurance.insuranceTerm || {};

              if (response?.data?.uploadPolicyDocumentResponses.length > 0) {
                result.insurance.insuranceTerm.policyDocuments =
                  response?.data?.uploadPolicyDocumentResponses?.map((doc) => ({
                    name: doc.fileName,
                    size: doc.fileSize,
                    type: doc.fileType,
                    filePath: doc.filePath,
                    isExisting: true,
                    content: null,
                  }));
              }

              if (response.data.createdAt !== null) {
                result.createdAt = response.data.createdAt;
              }
              setFormData(result);
              if (response.data.approvedAt !== null) {
                const objApproveUserDetail = {
                  approvedDate: moment
                    .utc(response.data.approvedAt)
                    .tz(dataConstant.centralAmericaTimeZone)
                    .format(dataConstant.dateFormatWithTime),
                  approvedBy: response.data.approvedBy,
                };
                setApproveUserDetail(objApproveUserDetail);
              }

              setIsApiCalled(true);
            }
          }),
      );
    } catch (error) {
      console.error("Error loading form data from IndexedDB:", error);
    }
  }, []);

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        flexWrap={{ xs: "wrap", md: "nowrap" }}
        gap={2}
      >
        <Stack
          direction="row"
          flex={{ sm: "1 1 0" }}
          width={{ xs: "100%", sm: "unset" }}
        >
          {userDetails?.roles === dataConstant.roles.admin && (
            <IconButton
              onClick={() => navigate("/wellskyclients")}
              color="primary"
            >
              <IconChevronLeft stroke={1.5} />
            </IconButton>
          )}
          <Stack direction="column" flex="1 1 0">
            {isApiCalled && (
              <Typography
                variant="h4"
                fontSize={{ xs: 20, sm: 24 }}
                color="default"
              >
                {formData?.clientInformation?.firstName || "-"}{" "}
                {formData?.clientInformation?.lastName || "-"} (
                {formData?.formSubmissionDetails?.communityLiaison || "-"})
              </Typography>
            )}
            <Typography
              variant="body1"
              color="default.light"
              sx={{
                span: {
                  color: lightPalette.default.main,
                },
              }}
            >
              {approveUserDetail.approvedBy !== "" && (
                <>
                  Approved on <span>{approveUserDetail.approvedDate}</span> by{" "}
                  <span>{approveUserDetail.approvedBy}</span>
                </>
              )}
            </Typography>
          </Stack>
        </Stack>
        {isApiCalled && (
          <GeneratePdf formData={formData} label={"Print"} isPrintIcon={true} />
        )}
      </Box>
      <Box
        display="flex"
        gap={3}
        flex="1 1 0"
        flexWrap={{ xs: "wrap", md: "nowrap" }}
        overflow="auto"
        p={1}
      >
        {/* Sidebar */}
        <Box
          sx={{
            minWidth: 225,
            bgcolor: lightPalette.background.paper,
            p: { xs: 1, md: 2 },
            boxShadow: "0 4px 20px rgba(194,196,199,0.25)",
            borderRadius: 2,
            overflow: "auto",
          }}
        >
          <List
            sx={{
              display: { xs: "flex", md: "block" },
              alignItems: "center",
              padding: 0,
              flexWrap: "wrap",
            }}
          >
            {menuItems.map((item) => (
              <ListItem
                key={item.key}
                selected={activeKey === item.key}
                onClick={() => setActiveKey(item.key)}
                sx={{
                  width: { xs: "50%", md: "100%" },
                  padding: { xs: "8px", md: "8px 12px" },
                  color:
                    activeKey === item.key ? "primary.main" : "default.main",
                  cursor: "pointer",
                  "& .MuiListItemIcon-root": {
                    color:
                      activeKey === item.key ? "primary.main" : "default.main",
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.label}
                  sx={{
                    margin: 0,

                    ".MuiListItemText-primary": {
                      lineHeight: 1.25,
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Content Area */}
        {isApiCalled && (
          <Box flex="1 1 0" overflow="auto">
            {/* You can write content for each section below */}
            {activeKey === "clientInfo" && (
              <ClientInfoDetail formData={formData} />
            )}
            {activeKey === "serviceSchedule" && (
              <ServiceScheduleDetail formData={formData} />
            )}
            {activeKey === "serviceDetails" && (
              <ServiceDetail formData={formData} />
            )}
            {activeKey === "serviceAddress" && (
              <ServiceAddressDetail formData={formData} />
            )}
            {activeKey === "careStatus" && (
              <CareStatusDetail formData={formData} />
            )}
            {activeKey === "authorizedContacts" && (
              <AuthorizedContactDetail formData={formData} />
            )}
            {activeKey === "emergencyPlanning" && (
              <EmergencyPlanningDetail formData={formData} />
            )}
            {activeKey === "documentation" && (
              <DocumentationDetail formData={formData} />
            )}
            {activeKey === "paymentDetails" && (
              <PaymentDetail formData={formData} />
            )}
            {activeKey === "billingInformation" && (
              <BillingInformation formData={formData} />
            )}
            {activeKey === "legalTerms" && (
              <LegalTermsDetail formData={formData} fromReview={false} />
            )}
            {activeKey === "reviewSubmit" && (
              <>
                <Typography variant="h4" color="default.main">
                  Review
                </Typography>
                <ReviewDetail formData={formData} />
              </>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

export default PatientDetail;
